import React, { useEffect, useRef, useState } from "react";
import { normalizeOptions } from "../../utils/miscelleneous";
import useAudioAnswer from "../../utils/useAudioAnswer";
import AudioPlayer from "../../utils/audioPlayer";

const ReadingSection = ({ section, flattenedQuestions = [], submitAnswer, onRequestNextSection, assignmentId }) => {
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const [index, setIndex] = useState(0);
  const [localAnswers, setLocalAnswers] = useState({});
  const [recTimer, setRecTimer] = useState(60);
  const [isPlaying, setIsPlaying] = useState(false);

  const {
    phase,
    isRecording,
    hasRecording,
    uploadedUrl,
    uploadedDuration,
    uploadError,
    beginRecording,
    finishRecording,
    retryUpload,
    recordAgain,
    stopRecording,
  } = useAudioAnswer({ assignmentId, filePrefix: "reading" });

  const isCompleted = (q) => {
    if (!q?.previous_answer?.answer) return false;
    const data = q.previous_answer.answer.answer_data;
    if (data?.status === "completed") return true;
    if (typeof data === "string" && data.trim() !== "") return true;
    if (typeof data === "object" && Object.keys(data).length > 0) return true;
    return false;
  };

  useEffect(() => {
    let nextIndex = index;
    while (
      nextIndex < flattenedQuestions.length &&
      isCompleted(flattenedQuestions[nextIndex])
    ) {
      nextIndex++;
    }
    if (nextIndex !== index) {
      if (nextIndex >= flattenedQuestions.length) {
        onRequestNextSection && onRequestNextSection();
      } else {
        setIndex(nextIndex);
      }
    }
  }, [section, index, flattenedQuestions]);

  useEffect(() => {
    setIndex(0);
    setLocalAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [section]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      stopRecording();
    };
  }, [section]);

  // Reset the recorder whenever we move to a new question.
  useEffect(() => {
    setRecTimer(60);
    setIsPlaying(false);
    recordAgain();
  }, [index]);

  if (!section) return <div className="language-right-section">No section data</div>;

  const current = flattenedQuestions[index];

  if (!current) return <div className="language-right-section">No questions</div>;

  const isParent = !!current._isParent;
  const parentPassage = isParent ? current.content?.passage : (flattenedQuestions.slice(0, index).reverse().find(p => p._isParent && p.groupId === current.groupId) || null);

  const handleMCQSelect = (assignment_question_id, opt) => {
    setLocalAnswers((s) => ({
      ...s,
      [assignment_question_id]: {
        answer_type: "multiple_choice",
        selected_option_id: opt.id,
        selected_option: `${opt.id}) ${opt.text}`
      }
    }));
  };

  const handleTextChange = (assignment_question_id, text) => {
    setLocalAnswers((s) => ({ ...s, [assignment_question_id]: { answer_type: "text_input", text_answer: text } }));
  };

  const startTimer = () => {
    setRecTimer(60);
    clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setRecTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          finishRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startRec = () => {
    setRecTimer(60);
    beginRecording(current);
    startTimer();
  };

  const stopRec = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    finishRecording();
  };

  const handleRecordAgain = () => {
    setRecTimer(60);
    setIsPlaying(false);
    recordAgain();
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
  };

  const handleSubmitRecording = () => {
    if (phase !== "ready" || !uploadedUrl) return;

    const answer = {
      answer_data: {
        type: "audio_recording",
        audio_url: uploadedUrl,
        duration_seconds: uploadedDuration,
        status: "completed",
        timestamp: new Date().toISOString(),
      }
    };

    submitAnswer({
      assignment_question_id: current.assignment_question_id,
      question_id: current.id,
      answer,
    });

    if (index < flattenedQuestions.length - 1) {
      setIndex(index + 1);
    } else {
      onRequestNextSection && onRequestNextSection();
    }
  };

  const handleSubmitMCQ = () => {
    const assignment_question_id = current.assignment_question_id;
    const local = localAnswers[assignment_question_id];
    if (!local) return;

    submitAnswer({
      assignment_question_id,
      question_id: current.id,
      answer: { answer_data: local.selected_option || local.text_answer },
    });

    if (index < flattenedQuestions.length - 1) {
      setIndex(index + 1);
    } else {
      onRequestNextSection && onRequestNextSection();
    }
  };

  const isAnswered = !!localAnswers[current.assignment_question_id];

  return (
    <>
      <div className="language-left-section reading" style={{ gap: "13px" }}>

        {/* Parent Passage */}
        {current._isParent && current.content?.question_type === "reading_comprehension_set" && (
          <div className="passage-box">
            <p style={{ maxWidth: '450px', width: '100%', overflow: 'auto' }}>{current.content?.paragraph}</p>
          </div>
        )}

        {/* Child Questions – show same passage */}
        {!current._isParent &&
          parentPassage &&
          parentPassage.content?.paragraph && (
            <div className="passage-box">
              <p style={{ maxWidth: '450px', width: '100%', overflow: 'auto' }}>{parentPassage.content.paragraph}</p>
            </div>
          )}

        {/* Reading Fluency - text to read aloud */}
        {current.content?.question_type === "reading_fluency_individual" && (
          <div className="passage-box">
            <p style={{ maxWidth: '450px', width: '100%', overflow: 'auto' }}>{current.text}</p>
          </div>
        )}
      </div>
      <div className="language-right-section">
        <div className="mcq">
          {/* PARENT ONLY: JUST CONTINUE BUTTON */}
          {current._isParent &&
            current.content?.question_type === "reading_comprehension_set" && (
              <>
                <p>{current.content.instructions}</p>
                <button
                  className="submit-btn"
                  onClick={() => {
                    submitAnswer({
                      assignment_question_id: current.assignment_question_id,
                      question_id: current.id,
                      answer: { answer_data: "viewed" }
                    });
                    setIndex(index + 1);
                  }}
                >
                  Continue
                </button>
              </>
            )}

          {/* CHILD MCQ QUESTIONS */}
          {!current._isParent && current.content?.options && (
            <>
              <h5>{current.text}</h5>

              <div className="option-grid">
                {normalizeOptions(current.content.options).map((opt, i) => {
                  const idKey = current.assignment_question_id || current.id;

                  const selected =
                    localAnswers[idKey]?.selected_option_id === opt.id;

                  return (
                    <div
                      key={i}
                      className={`opt-box ${selected ? "selected" : ""}`}
                      onClick={() => handleMCQSelect(idKey, opt)}
                    >
                      <span className="opt-label">{opt.id}</span>
                      <span className="opt-text">{opt.text}</span>
                    </div>
                  );
                })}
              </div>

              <button
                className={`submit-btn ${!isAnswered ? "disabled" : ""}`}
                disabled={!isAnswered}
                onClick={handleSubmitMCQ}
              >
                Submit
              </button>
            </>
          )}

          {/* TEXT INPUT QUESTIONS */}
          {!current._isParent &&
            !current.content?.options &&
            current.content?.question_type === "text_input" && (
              <>
                <h5>{current.text}</h5>
                <textarea
                  placeholder="Type your answer..."
                  onChange={(e) =>
                    handleTextChange(
                      current.assignment_question_id || current.id,
                      e.target.value
                    )
                  }
                />
                <button
                  className={`submit-btn ${!isAnswered ? "disabled" : ""}`}
                  disabled={!isAnswered}
                  onClick={handleSubmitMCQ}
                >
                  Submit
                </button>
              </>
            )}

          {/* READING FLUENCY RECORDING */}
          {current.content?.question_type === "reading_fluency_individual" && (
            <>
              <p className="instructions">{current.content.instructions}</p>

              <div className="speaking-right" style={{ textAlign: "center" }}>
                <p style={{ marginBottom: "0" }}>
                  {!hasRecording
                    ? "Record your response by clicking the mic below"
                    : "Listen to your recording or submit"}
                </p>

                {/* BEFORE RECORDING */}
                {!isRecording && !hasRecording && (
                  <div className="wight-mic-bg" style={{ cursor: "pointer", margin: "15px auto" }}
                    onClick={startRec}
                  >
                    <img title="Start Recording" src="/images/mic-icon.svg" alt="" width={56} height={56} />
                  </div>
                )}

                {/* DURING RECORDING */}
                {isRecording && (
                  <div className="wight-mic-bg" style={{ cursor: "pointer", margin: "15px auto" }}
                    onClick={stopRec}
                  >
                    <img title="Stop Recording" src="/images/reddot-img.svg" alt="" width={71} height={71} />
                  </div>
                )}

                {/* UPLOADING */}
                {!isRecording && phase === "uploading" && (
                  <div className="wight-mic-bg" style={{ margin: "15px auto", opacity: 0.6 }}>
                    <img title="Saving" src="/images/mic-icon.svg" alt="" width={56} height={56} />
                  </div>
                )}

                {/* UPLOAD ERROR */}
                {!isRecording && phase === "error" && (
                  <div className="wight-mic-bg" style={{ cursor: "pointer", margin: "15px auto" }} onClick={retryUpload}>
                    <img title="Retry upload" src="/images/refresh-outline.svg" alt="" width={40} height={40} />
                  </div>
                )}

                {/* AFTER UPLOAD — PLAY/PAUSE */}
                {!isRecording && phase === "ready" && uploadedUrl && (
                  <div className="wight-mic-bg" style={{ cursor: "pointer", margin: "15px auto" }} onClick={togglePlay}>
                    <img title="Play-Pause" src={isPlaying ? "/images/pause-icon.svg" : "/images/play-icon.svg"} alt="" width={56} height={56} />
                  </div>
                )}

                {/* Timer During Recording */}
                {isRecording && (<p style={{ marginBottom: "12px" }}>Recording… {recTimer}s left</p>)}

                {!isRecording && phase === "uploading" && (
                  <p style={{ marginBottom: "12px" }}>Saving your recording…</p>
                )}

                {/* Waveform */}
                {!isRecording && phase === "ready" && uploadedUrl && (
                  <AudioPlayer audioUrl={uploadedUrl} ref={audioRef} onPlayStateChange={(state) => setIsPlaying(state)} />
                )}

                {/* Submit / Retry */}
                {!isRecording && hasRecording && (
                  phase === "error" ? (
                    <button className="primary" onClick={retryUpload} style={{ marginTop: "12px" }}>
                      Retry upload
                    </button>
                  ) : (
                    <button
                      className="primary"
                      onClick={handleSubmitRecording}
                      disabled={phase !== "ready"}
                      style={{ marginTop: "12px" }}
                    >
                      {phase === "uploading" ? "Uploading…" : "Submit"}
                    </button>
                  )
                )}

                {/* Rerecord */}
                {!isRecording && hasRecording && (
                  <p
                    onClick={handleRecordAgain}
                    style={{ cursor: "pointer", marginTop: "6px", width: 'unset' }}
                  >
                    <img src="/images/refresh-outline.svg" alt="" width={16} height={16} /> Record Again
                  </p>
                )}

                {!isRecording && phase === "error" && (
                  <p className="error">{uploadError || "Upload failed. Please retry."}</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ReadingSection;
